import { CheckStateEnum } from '@app/core/enums/check/check-state.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { IRelatedAsset, RelatedAsset } from '@app/core/model/entities/asset/asset';
import { AssetsRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Document } from '@app/core/model/entities/document/document';
import { Entity } from '@app/core/model/entities/entity';
import { getValue, hasProperty, setValue } from '@app/shared/extra/utils';
import { Expose, Transform, Type } from 'class-transformer';

export class Check extends Entity implements AssetsRelatedEntity {
  public objectType = EntityTypeEnum.CHECK;

  public assetId: string;
  public type: string;

  @Expose({name: 'spaceIdsList'})
  public spaceIds: string[];
  @Type(() => Document) public documents: Document[] = [];
  @Transform(fn => CheckStateEnum[fn.value]) public state: CheckStateEnum;

  @Expose()
  public toString(): string {
    return this.computedProperties?.['displayName'] ?? this.type?.toString();
  }

  public get result(): string {
    return this.properties['result_' + this.type];
  }

  // Properties to be renamed
  protected static inputMapping = {
    spaces: {input: ['computedProperties', 'spaces'], output: 'spaceIds'}
  };

  /**
   * Some GraphQL inputs require different keys than those available in the Form State Service
   * since those keys correspond to field codes, so we need to replace them accordingly
   * @param obj the Graphql input object
   */
  public static transformPropertiesForInput(obj: Record<string, unknown>): void {
    // Transform input, mapping keys as defined in the inputMapping above
    for (const inputMappingElement in Check.inputMapping) {
      if (hasProperty(obj, Check.inputMapping[inputMappingElement].input)) {
        const source = getValue(obj, Check.inputMapping[inputMappingElement].input);
        setValue(obj, [Check.inputMapping[inputMappingElement].output], source);
      }
    }

    // Clean up by removing older keys
    for (const inputMappingElement in Check.inputMapping) {
      delete obj[Check.inputMapping[inputMappingElement].input.first()];
    }
  }

  /**
   * Get the related asset inside the Check computed properties.
   * @return List of related assets
   */
  @Expose()
  public get relatedAssets(): RelatedAsset[] {
    return this.computedProperties?.['asset']
        ?.let((asset: IRelatedAsset) => [new RelatedAsset(asset)])
      ?? [];
  }
}

export interface CheckInput {
  assetId?: string;
  type?: string;
  spaceIds?: string[];
  properties?: Record<string, any>;
}
